import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, LockKeyhole, CheckCircle2, KeyRound } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const GOLD_INK = '#8F650F';
const TERRA = '#E07A5F';

// Admin-Panel: einzelne Module für einen Teilnehmer freischalten (auch außer der Reihe).
// userId optional — ohne userId zeigt das Modal zuerst eine Teilnehmer-Auswahl (Kurs-Kontext).
export const AdminModuleUnlockModal = ({ userId, userName, courseId, courseTitle, getAuthHeader, onClose }) => {
  const [selectedUserId, setSelectedUserId] = useState(userId || '');
  const [selectedUserName, setSelectedUserName] = useState(userName || '');
  const [userOptions, setUserOptions] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (userId) return;
    axios.get(`${API}/admin/users`, getAuthHeader())
      .then((r) => {
        const list = (r.data || []).filter((u) => !u.archived && u.role !== 'admin' && (u.assigned_courses || []).includes(courseId));
        setUserOptions(list);
      })
      .catch(() => setUserOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, courseId]);

  const loadOverview = useCallback(async (uid) => {
    if (!uid) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users/${uid}/courses/${courseId}/unlock-overview`, getAuthHeader());
      setOverview(r.data.modules || []);
    } catch {
      toast.error('Fehler beim Laden der Modul-Übersicht');
      setOverview([]);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  useEffect(() => { if (selectedUserId) loadOverview(selectedUserId); }, [selectedUserId, loadOverview]);

  const unlock = async (mod) => {
    setBusyId(mod.module_id);
    try {
      await axios.post(
        `${API}/admin/users/${selectedUserId}/modules/${mod.module_id}/force-unlock?reason=${encodeURIComponent('Admin-Freischaltung außer der Reihe')}`,
        {}, getAuthHeader()
      );
      toast.success(`„${mod.title}" freigeschaltet`);
      await loadOverview(selectedUserId);
    } catch {
      toast.error('Freischalten fehlgeschlagen');
    }
    setBusyId(null);
  };

  const revoke = async (mod) => {
    setBusyId(mod.module_id);
    try {
      await axios.post(`${API}/admin/users/${selectedUserId}/modules/${mod.module_id}/revoke-unlock`, {}, getAuthHeader());
      toast.success('Freischaltung zurückgenommen');
      await loadOverview(selectedUserId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zurücknehmen fehlgeschlagen');
    }
    setBusyId(null);
  };

  return createPortal(
    <div className="fixed inset-0 bg-espresso/50 backdrop-blur-[2px] flex items-center justify-center z-[60] p-4" data-testid="unlock-modal">
      <div className="bg-cream-50 rounded-2xl border border-cream-300 w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl animate-fade-in">
        <div className="flex items-start justify-between p-6 border-b border-cream-300">
          <div>
            <p className="font-mono text-[9.5px] uppercase font-semibold mb-1" style={{ letterSpacing: '0.22em', color: GOLD_INK }}>
              Module freischalten
            </p>
            <h2 className="font-display text-[22px] text-espresso leading-tight">{courseTitle || courseId}</h2>
            {selectedUserName && <p className="text-[13px] text-sand-600 mt-0.5">für {selectedUserName}</p>}
          </div>
          <button onClick={onClose} className="text-sand-500 hover:text-espresso p-1" data-testid="unlock-modal-close" aria-label="Schließen">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {!userId && (
            <div className="mb-5">
              <label className="block text-[12px] text-sand-600 mb-1.5">Teilnehmer:in wählen</label>
              <select
                value={selectedUserId}
                onChange={(e) => {
                  setSelectedUserId(e.target.value);
                  const u = (userOptions || []).find((x) => x.id === e.target.value);
                  setSelectedUserName(u ? (u.name || u.username) : '');
                }}
                className="w-full px-3 py-2.5 bg-cream-100 border border-cream-300 rounded-xl text-[13.5px] text-sand-800 focus:outline-none focus:border-terra"
                data-testid="unlock-user-select"
              >
                <option value="">— Teilnehmer:in auswählen —</option>
                {(userOptions || []).map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.username} (@{u.username})</option>
                ))}
              </select>
              {userOptions && userOptions.length === 0 && (
                <p className="text-[12.5px] text-sand-500 italic mt-2">Diesem Kurs sind keine Teilnehmer zugewiesen.</p>
              )}
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-terra" />
            </div>
          )}

          {!loading && overview && (
            <div className="space-y-1" data-testid="unlock-module-list">
              {overview.map((mod) => (
                <div
                  key={mod.module_id}
                  className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-cream-100 transition-colors"
                  data-testid={`unlock-row-${mod.module_id}`}
                >
                  {mod.is_unlocked ? (
                    <CheckCircle2 className="w-[17px] h-[17px] flex-shrink-0" style={{ color: GOLD_INK }} />
                  ) : (
                    <LockKeyhole className="w-[17px] h-[17px] flex-shrink-0 text-sand-400" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-[13.5px] leading-snug truncate ${mod.is_unlocked ? 'text-espresso' : 'text-sand-600'}`}>
                      {mod.title}
                    </p>
                    {mod.manually_unlocked && (
                      <p className="font-mono text-[9px] uppercase font-semibold mt-0.5" style={{ letterSpacing: '0.16em', color: GOLD_INK }}>
                        Admin-freigeschaltet
                      </p>
                    )}
                  </div>
                  {busyId === mod.module_id ? (
                    <Loader2 className="w-4 h-4 animate-spin text-terra flex-shrink-0" />
                  ) : mod.manually_unlocked ? (
                    <button
                      onClick={() => revoke(mod)}
                      className="text-[11.5px] text-sand-500 hover:text-terra flex-shrink-0"
                      data-testid={`revoke-unlock-${mod.module_id}`}
                    >
                      Zurücknehmen
                    </button>
                  ) : !mod.is_unlocked ? (
                    <button
                      onClick={() => unlock(mod)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase flex-shrink-0 transition-colors"
                      style={{ border: `1.5px solid ${TERRA}`, color: TERRA, letterSpacing: '0.12em' }}
                      data-testid={`force-unlock-btn-${mod.module_id}`}
                    >
                      <KeyRound className="w-3.5 h-3.5" />
                      Freischalten
                    </button>
                  ) : (
                    <span className="font-mono text-[9px] uppercase font-semibold text-sand-400 flex-shrink-0" style={{ letterSpacing: '0.16em' }}>offen</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {!loading && !overview && selectedUserId === '' && userId === undefined && (
            <p className="text-[13px] text-sand-500 italic">Bitte zuerst eine:n Teilnehmer:in auswählen.</p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-cream-300">
          <p className="text-[11.5px] text-sand-500 leading-relaxed">
            Freischalten öffnet nur die Reihenfolge-Sperre — Aufgaben und Quiz des Kapitels bleiben Voraussetzung für den Abschluss.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AdminModuleUnlockModal;
